import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { FaTimes, FaArrowLeft, FaArrowRight } from "react-icons/fa";

const API_URL = "https://api.lii.com.co:5101/api/photoAlbum";

const PhotoAlbum = () => {
  const [photos, setPhotos] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(null); // Track which photo is opened

  // Fetch photos from the backend
  useEffect(() => {
    axios.get(API_URL)
      .then(response => setPhotos(response.data))
      .catch(error => console.error("Error fetching photos:", error));
  }, []);

  // Handle file upload
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("photo", file);

    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPhotos([response.data, ...photos]);
      setSelectedFile(null);
    } catch (error) {
      console.error("Error uploading photo:", error);
    }
  };

  // Delete photo
  const handleDeletePhoto = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`);
      setPhotos(photos.filter(photo => photo._id !== id));
    } catch (error) {
      console.error("Error deleting photo:", error);
    }
  };

  // Open full-screen photo viewer
  const openPhotoViewer = (index) => {
    setCurrentPhotoIndex(index);
  };

  // Close full-screen viewer
  const closePhotoViewer = () => {
    setCurrentPhotoIndex(null);
  };

  // Navigate to previous photo
  const prevPhoto = () => {
    setCurrentPhotoIndex((prevIndex) => (prevIndex > 0 ? prevIndex - 1 : photos.length - 1));
  };

  // Navigate to next photo
  const nextPhoto = () => {
    setCurrentPhotoIndex((prevIndex) => (prevIndex < photos.length - 1 ? prevIndex + 1 : 0));
  };

  // Handle keyboard events for navigation
  const handleKeyDown = useCallback((event) => {
    if (currentPhotoIndex !== null) {
      if (event.key === "ArrowLeft") prevPhoto();
      if (event.key === "ArrowRight") nextPhoto();
      if (event.key === "Escape") closePhotoViewer();
    }
  }, [currentPhotoIndex, photos.length]);

  // Attach keyboard events when photo viewer is open
  useEffect(() => {
    if (currentPhotoIndex !== null) {
      document.addEventListener("keydown", handleKeyDown);
    } else {
      document.removeEventListener("keydown", handleKeyDown);
    }
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [currentPhotoIndex, handleKeyDown]);

  return (
    <div className="bg-gray-100 min-h-screen">
      {/* Header */}
      <header className="bg-blue-600 text-white py-4 shadow-md">
        <div className="container mx-auto flex items-center px-6">
          <h1 className="text-2xl font-bold">
            <Link to="/dashboard" className="text-white hover:text-gray-300">lii</Link>
          </h1>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-6 bg-white rounded-lg shadow-lg mt-8">
        <h1 className="text-3xl font-bold mb-6 text-center text-blue-600">📷 Photo Album</h1>

        {/* Upload Section */}
        <div className="mb-6 text-center">
          <input type="file" id="file-upload" accept="image/*" onChange={handleFileUpload} className="hidden" />
          <label
            htmlFor="file-upload"
            className="bg-blue-500 text-white py-3 px-6 rounded-lg cursor-pointer hover:bg-blue-600 transition inline-flex items-center justify-center shadow-md"
          >
            📤 Partagez une photo
          </label>
          {selectedFile && <p className="text-sm text-gray-500 mt-2">{selectedFile}</p>}
        </div>

        {/* Photo Gallery */}
        {photos.length > 0 ? (
          <div className="grid grid-cols-3 gap-4">
            {photos.map((photo, index) => (
              <div key={photo._id} className="relative">
                <img
                  src={`https://api.lii.com.co:5101${photo.url}`}
                  alt="Uploaded"
                  className="w-full h-40 object-cover rounded-lg shadow-md cursor-pointer"
                  onClick={() => openPhotoViewer(index)}
                />
                <button
                  onClick={() => handleDeletePhoto(photo._id)}
                  className="absolute top-2 right-2 bg-red-600 text-white text-xs px-2 py-1 rounded-full hover:bg-red-700"
                >
                  ❌
                </button>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500 text-center mt-4">No photos uploaded yet.</p>
        )}
      </div>

      {/* Full-Screen Photo Viewer */}
      {currentPhotoIndex !== null && (
        <div className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50">
          <button className="absolute top-5 right-5 text-white text-2xl" onClick={closePhotoViewer}>
            <FaTimes />
          </button>

          <button className="absolute left-5 text-white text-3xl" onClick={prevPhoto}>
            <FaArrowLeft />
          </button>

          <img
            src={`https://api.lii.com.co:5101${photos[currentPhotoIndex].url}`}
            alt="Full-Screen"
            className="max-w-full max-h-screen rounded-lg shadow-lg"
          />

          <button className="absolute right-5 text-white text-3xl" onClick={nextPhoto}>
            <FaArrowRight />
          </button>
        </div>
      )}
    </div>
  );
};

export default PhotoAlbum;